const VIEWBOX_SIZE = 200;
const CENTER = VIEWBOX_SIZE / 2;
const RADIUS = 96;
const IMAGE_RADIUS_RATIO = 0.56;
// Labels live in the outer band of each slice, just inside the rim, where a
// slice is widest. Closer to the hub the slices narrow so much that neighbouring
// names run into each other.
const LABEL_RADIUS_RATIO = 0.88;
const LABEL_FONT_SIZE = 7; // keep in sync with .spin-wheel__slice-label
const LABEL_CHAR_WIDTH_RATIO = 0.62;
const LABEL_CHORD_USAGE = 0.85;
const MAX_IMAGE_SIZE = 40;

const toRadians = (degrees) => (degrees * Math.PI) / 180;

// Angles run clockwise from the top of the wheel, which is where the arrow sits.
const pointOnCircle = (angle, radius) => ({
  x: CENTER + radius * Math.sin(toRadians(angle)),
  y: CENTER - radius * Math.cos(toRadians(angle)),
});

const buildSlicePath = (startAngle, endAngle) => {
  const start = pointOnCircle(startAngle, RADIUS);
  const end = pointOnCircle(endAngle, RADIUS);
  const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

  return `M ${CENTER} ${CENTER} L ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 ${largeArcFlag} 1 ${end.x} ${end.y} Z`;
};

// Width of a slice measured across at the given radius.
const getChordWidth = (sliceAngle, radius) => 2 * radius * Math.sin(toRadians(Math.min(sliceAngle, 180) / 2));

// A single slice never gets a picture wider than the room it has, so the wheel
// stays readable whether it holds three items or twelve.
const getImageSize = (sliceAngle) => {
  const sliceWidth = getChordWidth(sliceAngle, RADIUS * IMAGE_RADIUS_RATIO);

  return Math.min(MAX_IMAGE_SIZE, Math.max(sliceWidth * 0.78, 14));
};

// Long names are squeezed to the width available in their slice instead of
// spilling over into the neighbours. Short names are left alone.
const getLabelLengthProps = (name, sliceAngle) => {
  const maxWidth = getChordWidth(sliceAngle, RADIUS * LABEL_RADIUS_RATIO) * LABEL_CHORD_USAGE;
  const estimatedWidth = name.length * LABEL_FONT_SIZE * LABEL_CHAR_WIDTH_RATIO;

  if (estimatedWidth <= maxWidth) {
    return {};
  }

  return { textLength: maxWidth.toFixed(1), lengthAdjust: 'spacingAndGlyphs' };
};

export default function SpinWheelGraphic({ items, rotation, spinning, transition, label = 'Spin the Wheel' }) {
  const sliceAngle = 360 / items.length;
  const imageSize = getImageSize(sliceAngle);
  const showLabels = sliceAngle >= 30;

  return (
    <svg
      viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
      className="spin-wheel__image"
      role="img"
      aria-label={label}
      style={{
        transform: `rotate(${rotation}deg)`,
        transition: spinning ? transition : 'none',
      }}
    >
      {items.map((item, index) => {
        const startAngle = index * sliceAngle;
        const midAngle = startAngle + sliceAngle / 2;
        const imageCenter = pointOnCircle(midAngle, RADIUS * IMAGE_RADIUS_RATIO);
        const labelCenter = pointOnCircle(midAngle, RADIUS * LABEL_RADIUS_RATIO);
        // `label` is the name in the user's language; `name` is the stored key.
        const sliceLabel = item.label ?? item.name;

        return (
          <g key={item.id}>
            <path
              d={buildSlicePath(startAngle, startAngle + sliceAngle)}
              fill={item.color}
              stroke="#ffffff"
              strokeWidth="1"
            />
            <image
              href={item.image}
              x={imageCenter.x - imageSize / 2}
              y={imageCenter.y - imageSize / 2}
              width={imageSize}
              height={imageSize}
              preserveAspectRatio="xMidYMid meet"
              transform={`rotate(${midAngle} ${imageCenter.x} ${imageCenter.y})`}
            >
              <title>{sliceLabel}</title>
            </image>
            {showLabels && (
              <text
                x={labelCenter.x}
                y={labelCenter.y}
                textAnchor="middle"
                dominantBaseline="middle"
                className="spin-wheel__slice-label"
                transform={`rotate(${midAngle} ${labelCenter.x} ${labelCenter.y})`}
                {...getLabelLengthProps(sliceLabel, sliceAngle)}
              >
                {sliceLabel}
              </text>
            )}
          </g>
        );
      })}
      <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="none" stroke="#009794" strokeWidth="3" />
      <circle cx={CENTER} cy={CENTER} r="11" fill="#ffffff" stroke="#009794" strokeWidth="3" />
    </svg>
  );
}
